import React, { useEffect } from "react";
import { useLocation, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Button, ProgressIndicator, EmptyItem, Pager } from "rhombus-react";

import { CREATIVE_ROW_COUNT_OPTIONS, STATUS_NOT_FOUND } from "utils/constants";
import { useCollectionFiltering } from "utils/DataGrid";

import { QrCodes } from "reducers/QrCodes";
import { QrDesigns } from "reducers/QrDesigns";
import { hasActiveFilters } from "reducers/common/ColumnFilters";

import { QR_CODES_COLUMNS } from "widgets/grid/columns/QrCodesColumns";
import FilterTagList from "widgets/toolbars/FilterTagList";

import useImageFallback from "hooks/useImageFallback";

import Toolbar from "./QrCodesToolbar";
import Grid from "./Grid";
import Modals from "./Modals";
import { push } from "connected-react-router";
import Permissioned from "widgets/Permissioned";

const action = "Upload Assets";
const domain = "Creative View";

import "./QrCodesGrid.css";

export default function QrCodesGrid() {
	const { libraryId, designId  } = useParams();
	const dispatch = useDispatch();
	const { pathname: currentPath } = useLocation();
	const openSubmitQrCodeModal = () => { dispatch(push(`${currentPath}modal/submit-qr-code/`)); };
	const qrState = useSelector(state => state.qrCodes);
	const qrDesignsState = useSelector(state => state.qrDesigns);

	const { items, isLoading, totalItems, totalPages, hasErrorLoading } = qrState || {};
	const { currentDesign, errorStatus } = qrDesignsState || {};

	useEffect(() => {
		dispatch(QrDesigns.clearColumnFilters());
		dispatch(QrDesigns.getQrDesignById(libraryId, designId));
	}, [designId]);

	const {
		filterInfo: { page, rpp, sort, searchTerm },
		columnFilters,
	} = useCollectionFiltering(
		qrState,
		QrCodes.setAdvancedFilter,
		() => {
			dispatch(QrCodes.clearQrCodes()).then(() => {
				dispatch(QrCodes.getQrCodes(libraryId, designId));
			});
		}
	);

	const handleSearch = newSearchTerm => dispatch(QrCodes.setSearchTerm(newSearchTerm));

	function resetColumnFilters() {
		dispatch(QrCodes.clearColumnFilters());
	}

	function retryLoading() {
		dispatch(QrDesigns.getQrDesignById(libraryId, designId));
		dispatch(QrCodes.getQrCodes(libraryId, designId));
	}

	let hasActive = hasActiveFilters(qrState)
		, hasFiltersAppliedClass = hasActive ? "has-advanced-filters-applied" : ""
		;

	const { name: designName, previewImageUrl, numQrCodes } = currentDesign || {};
	const { imageSrc } = useImageFallback(previewImageUrl, "/images/qr-code-placeholder@3x.png");

	let emptyProps = {
		className: "empty-qr-codes no-qr-codes",
		title: "QR code design could not be found",
		//The message is an empty string to avoid the default message from the EmptyItem component
		message: " ",
		src: "/images/404.svg",
		buttons: (
			<Link to={`/library/${libraryId}/qr-designs/`}>
				<Button>View QR Code Designs</Button>
			</Link>
		),
	};

	const PermissionedGenerateQrCodeButton = (
		<Permissioned action={action} domain={domain} key={"generate-qr-code"}>
			<Button key="generate" onClick={() => openSubmitQrCodeModal()}>Generate QR Code</Button>
		</Permissioned>
	);

	if (hasErrorLoading && errorStatus !== STATUS_NOT_FOUND) {
		emptyProps = {
			className: "empty-qr-codes no-qr-codes",
			title: "There was a problem loading QR Codes",
			message: " ",
			icon: "fas fa-bug",
			buttons: [
				<Button key="reload" icon="refresh"
					onClick={retryLoading}>
					Re-Try
				</Button>
			],
		};
	} else if (searchTerm) {
		emptyProps = {
			className: "empty-qr-codes no-search-results",
			title: "No search results to display",
			message: "Try adjusting your search to find what you are looking for",
			src: "/images/noSearch.svg",
			buttons: [
				<Button key="clear" onClick={() => dispatch(QrCodes.setSearchTerm(""))}>Clear Search</Button>,
				PermissionedGenerateQrCodeButton
			],
		};
	} else if (hasActive) {
		emptyProps = {
			className: "empty-qr-codes",
			title: "No results to display",
			message: "Try adjusting your filters to find what you are looking for",
			src: "/images/noFiles.svg",
			buttons: [
				<Button key="clear" onClick={resetColumnFilters}>Clear All Filters</Button>,
				PermissionedGenerateQrCodeButton
			],
		};
	} else if (currentDesign) {
		emptyProps = {
			className: "empty-qr-codes no-qr-codes",
			title: `No QR codes generated for "${designName}"`,
			//The message is an empty string to avoid the default message from the EmptyItem component
			message : " ",
			src: imageSrc,
			buttons: PermissionedGenerateQrCodeButton,
		};
	}


	const pagerProps={
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: totalPages,
		maxItems: totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,
		disabled: isLoading,

		onPageChange: newPage => dispatch(QrCodes.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(QrCodes.setRPP(newRPP)),
	};

	const refreshGrid = () => {
		const promises = [
			dispatch(QrDesigns.getQrDesignById(libraryId, designId)),
			dispatch(QrCodes.getQrCodes(libraryId, designId))
		];
		return Promise.all(promises);
	};

	const $filterList = (
		<FilterTagList
			key="filter-tags"
			Reducer = {QrCodes}
			filters = {columnFilters}
			resetFilters={resetColumnFilters}
		/>
	);

	return (
		<>
			<Modals
				key				= "modals"
				libraryId 		= {libraryId}
				designId		= {designId}
				previewImageUrl = {previewImageUrl}
				onSuccess 		= {refreshGrid}
			/>
			<div className={`${hasFiltersAppliedClass} qr-code-designs-div`}>
				<ProgressIndicator isLoading={isLoading} />
				<Toolbar
					key="toolbar"
					designId={designId}
					designName={designName}
					libraryId={libraryId}
					numQrCodes={numQrCodes}
					searchTerm={searchTerm}
					handleSearch={handleSearch}
					onClearFilters={resetColumnFilters}
				/>
				{hasActive && $filterList}
				{!isLoading && (!currentDesign || numQrCodes === 0 || items.length === 0) && (
					<EmptyItem {...emptyProps} />
				)}
				{currentDesign && numQrCodes > 0 && items.length > 0 && (
					<div className="qr-code-grid" data-testid="qrcodes-grid">
						<div className="qr-grid-header qr-grid-header-img">
							<img
								src={imageSrc}
								alt="Logo"
								className="qr-design-image"
							/>
						</div>
						<Permissioned action={action} domain={domain} key={"generate-qr-code-toolbar"}>
							<div className="qr-grid-header qr-grid-header-buttons">
								<Button
									icon="addCircleSolid"
									className="add-qr-code-button"
									size="small"
									onClick={() => openSubmitQrCodeModal()}
								>
									Generate QR Code
								</Button>
							</div>
						</Permissioned>
						<Grid columns={QR_CODES_COLUMNS} qrCodes={items} sort={sort} />
						<Pager {...pagerProps} />
					</div>
				)}
			</div>
		</>
	);
}
